/**
 * High-level auth-хук для компонентов (Stage 5.X.2).
 *
 * Один `useAuth()` hook возвращает всё, что нужно UI:
 *   - `user`, `isAuthenticated`, `isLoading`, `error`
 *   - `login()` — попытаться залогиниться через initData (auto-извлекается)
 *   - `logout()` — выйти и закрыть WebApp
 *
 * Никакого manual управления токенами — это делают `http.ts` + store.
 *
 * См. usage: `apps/miniapp/src/components/AuthGuard.tsx`.
 */

import { useCallback } from 'react';

import { loginWithInitData, logoutAuth } from '@/api/auth';
import { ApiError, NetworkError } from '@/api/types';
import { closeWebApp, getInitData, hapticNotification } from '@/lib/telegram';
import {
  selectIsAuthenticated,
  selectStatus,
  selectUser,
  useAuthStore,
  type AuthStatus,
} from '@/stores/auth';
import type { UserPublicView } from '@/api/types';

export interface UseAuthResult {
  user: UserPublicView | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Запустить login через `Telegram.WebApp.initData`. Идемпотентно. */
  login(): Promise<void>;
  /** Logout + закрыть WebApp. */
  logout(): Promise<void>;
}

/**
 * Главный auth-хук.
 *
 * При первом монтировании AuthGuard вызывает `login()` — это
 * единственная точка инициализации сессии. Повторные вызовы безопасны
 * (если уже залогинен — no-op).
 */
export function useAuth(): UseAuthResult {
  const user = useAuthStore(selectUser);
  const status = useAuthStore(selectStatus);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const error = useAuthStore((s) => s.error);

  const login = useCallback(async (): Promise<void> => {
    const store = useAuthStore.getState();
    if (store.status === 'authenticated') {
      return;
    }
    if (store.status === 'loading') {
      // Параллельный вызов login() — игнорируем (первый завершит state).
      return;
    }

    store.setLoading();

    const initData = getInitData();
    if (initData === null) {
      store.setError(
        'Telegram WebApp.initData недоступен. Откройте MiniApp через бота.',
      );
      return;
    }

    try {
      const response = await loginWithInitData(initData);
      store.setSession({
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        expiresIn: response.expires_in,
        user: response.user,
      });
    } catch (err) {
      const message = describeAuthError(err);
      store.setError(message);
      hapticNotification('error');
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = useAuthStore.getState().refreshToken;

    try {
      // Best-effort: даже если backend упадёт, локально считаем logout успешным.
      await logoutAuth(refreshToken);
    } catch {
      // Silent — store очистится ниже, юзер де-факто разлогинен.
    }

    useAuthStore.getState().clear();
    hapticNotification('success');
    closeWebApp();
  }, []);

  return {
    user,
    status,
    isAuthenticated,
    isLoading: status === 'loading',
    error,
    login,
    logout,
  };
}

/**
 * Перевести ApiError / NetworkError / Error в человекочитаемое сообщение.
 *
 * Stage 5.X.9 заменит на i18n-локализацию через react-i18next.
 */
function describeAuthError(err: unknown): string {
  if (ApiError.is(err)) {
    switch (err.code) {
      case 'AUTH_INVALID_INIT_DATA':
        return 'Не удалось проверить подпись Telegram. Попробуйте переоткрыть MiniApp через бота.';
      case 'AUTH_STALE_INIT_DATA':
        return 'Срок сессии Telegram истёк. Закройте MiniApp и откройте заново.';
      case 'AUTH_USER_DELETED':
        return 'Этот аккаунт удалён. Если это ошибка — обратитесь в поддержку.';
      default:
        return err.message || 'Ошибка авторизации.';
    }
  }
  if (err instanceof NetworkError) {
    return 'Нет связи с сервером. Проверьте интернет-соединение.';
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Неизвестная ошибка.';
}
